import { Button, ScrollView, TextArea, Toolbar, Tooltip } from 'devextreme-react';
import { useCallback, useEffect, useState } from 'react';
import Popup, { ToolbarItem } from 'devextreme-react/popup';
import { useAppDispatch, useAppSelector } from '../../../../../contexts/hooks';
import { showModalMultiline } from '../../../../../contexts/slices/orders';
import { useScreenSize } from '../../../../../utils/media-query';
import { newToast } from '../../../..';
import { Item } from 'devextreme-react/toolbar';
import { resButtonsClear, resGridStatus1, resGridStatus2, resGridStatus3, resGridStatus4, resGridStatus5, resButtonsPin } from '../../../../../images/icons';
import { ResultEstadoResult } from '../../../../../types/types.orders';
import { UseApiResults } from '../../../../../api/api.results';

export const ModalResultMultiline = () => {
  const dataModal = useAppSelector((x) => x.globalOrders.CurrentOrder.ModalResultMultiline);
  const Prm_ResC = useAppSelector((x) => x.userInfo.Permissions?.Prm_ResC) || false;
  const dispatch = useAppDispatch();
  const { isMedium, isXSmall, isSmall } = useScreenSize();
  const { putResult } = UseApiResults();
  const [textValue, setTextValue] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isChanged, setIsChanged] = useState<boolean>(false);

  useEffect(() => {
    if (!dataModal?.Visible) {
      setTextValue('');
      setIsChanged(false);
      return;
    }

    if (dataModal?.Res_Resultado) {
      setTextValue(dataModal.Res_Resultado);
      setIsChanged(false);
    }
  }, [dataModal]);

  const handleClose = useCallback(
    (update: boolean) => {
      dispatch(
        showModalMultiline({
          Visible: false,
          An_Id: -1,
          Res_Id: -1,
          An_Nombre: '',
          Res_Estado: ResultEstadoResult.SinResultado,
          Res_Resultado: '',
          Update: update,
          Pc_Id: -1,
        })
      );
      setTextValue('');
      setIsChanged(false);
    },
    [dispatch]
  );

  const handleCancel = useCallback(() => {
    handleClose(false);
  }, [handleClose]);

  const handleSave = useCallback(() => {
    if (!dataModal) return;

    setLoading(true);

    putResult({
      keys: [dataModal.Res_Id],
      Result: textValue,
    })
      .then((res) => {
        if (res) {
          newToast('SUCCESS', 'Resultado actualizado correctamente', 3);
          setIsChanged(false);
          handleClose(true);
        } else {
          newToast('ERROR', 'Error al actualizar el resultado', 3);
        }
      })
      .catch(() => {
        newToast('ERROR', 'Error al actualizar el resultado', 3);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dataModal, textValue, handleClose, putResult]);

  const handleClear = useCallback(() => {
    setTextValue('');
    setIsChanged(true);
  }, []);

  const handleTextChange = useCallback((e: any) => {
    setTextValue(e.value);
    setIsChanged(true);
  }, []);

  const getIconStatus = () => {
    switch (dataModal?.Res_Estado) {
      case ResultEstadoResult.SinResultado:
      default:
        return resGridStatus1;
      case ResultEstadoResult.ConResultado:
        return resGridStatus2;
      case ResultEstadoResult.Validado:
        return resGridStatus3;
      case ResultEstadoResult.Impreso:
        return resGridStatus4;
      case ResultEstadoResult.OtrasFuentes:
        return resGridStatus5;
      case ResultEstadoResult.ValidadoBlanco:
        return resButtonsPin;
    }
  };

  const getTextStatus = () => {
    switch (dataModal?.Res_Estado) {
      case ResultEstadoResult.SinResultado:
      default:
        return 'SIN RESULTADO';
      case ResultEstadoResult.ConResultado:
        return 'CON RESULTADO';
      case ResultEstadoResult.Validado:
        return 'VALIDADO';
      case ResultEstadoResult.Impreso:
        return 'IMPRESO';
      case ResultEstadoResult.OtrasFuentes:
        return 'OTRAS FUENTES';
      case ResultEstadoResult.ValidadoBlanco:
        return 'FIJADO EN BLANCO';
    }
  };

  const isReadOnly = !Prm_ResC || (dataModal?.Res_Estado ?? ResultEstadoResult.SinResultado) >= ResultEstadoResult.Validado;

  return (
    <Popup
      visible={dataModal?.Visible || false}
      onHidden={handleCancel}
      fullScreen={isMedium || isSmall || isXSmall}
      width={1200}
      height={'95%'}
      title={dataModal?.An_Nombre || ''}
      showTitle={true}
      showCloseButton={true}>
      <ScrollView width='100%' height='100%'>
        <Toolbar>
          <Item widget='dxButton' locateInMenu='auto' location='before'>
            <Button icon={getIconStatus()} text={getTextStatus()} stylingMode='text' />
          </Item>
          <Item location='before' locateInMenu='auto'>
            <div className='separator' />
          </Item>
          <Item location='before' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button
              disabled={isReadOnly}
              id='ClearMultilineResult'
              icon={resButtonsClear}
              text='Clarear resultado'
              onClick={handleClear}
              stylingMode='text'
            />
            <Tooltip target={'#ClearMultilineResult'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Limpiar resultado
            </Tooltip>
          </Item>
        </Toolbar>

        <div style={{ padding: '20px' }}>
          <TextArea
            value={textValue}
            onValueChanged={handleTextChange}
            readOnly={isReadOnly}
            height={500}
            maxLength={10000}
            placeholder={isReadOnly ? 'Resultado en modo solo lectura' : 'Ingrese el texto del resultado multilínea...'}
            stylingMode='outlined'
            showClearButton={false}
          />
          {!isReadOnly && (
            <div style={{ marginTop: '10px', color: '#666', fontSize: '12px' }}>
              Caracteres: {textValue?.length || 0} / 10000
            </div>
          )}
        </div>
      </ScrollView>
      <ToolbarItem widget='dxButton' toolbar='bottom' location='after' options={{ text: 'CANCELAR', type: 'normal', onClick: handleCancel }} />
      {dataModal && dataModal?.Res_Estado < ResultEstadoResult.Validado && (
        <ToolbarItem
          widget='dxButton'
          toolbar='bottom'
          location='after'
          options={{
            text: loading ? 'PROCESANDO...' : 'CONFIRMAR',
            stylingMode: 'contained',
            type: 'default',
            onClick: handleSave,
          }}
        />
      )}
    </Popup>
  );
};
